import { useAuth0 } from '@auth0/auth0-react'
import { useEffect } from 'react'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.midwestrailbirds.com'

export interface LedgerEntry {
  id: string
  title: string
  body: string
  scribe: string // Instead of "Author"
  tags: string[]
  images?: string[] // Optional photos for the adventure
  createdAt: string
  updatedAt: string
  createdBy: string
}

export interface CreateLedgerEntryData {
  title: string
  body: string
  scribe: string
  tags: string[]
  images?: string[] // Optional photos for the adventure
}

export interface UpdateLedgerEntryData {
  title?: string
  body?: string
  scribe?: string
  tags?: string[]
  images?: string[] // Optional photos for the adventure
}

export interface LedgerEntriesResponse {
  entries: LedgerEntry[]
  count: number
  hasMore: boolean
  nextKey?: string
}

export interface LedgerSearchParams {
  limit?: number
  lastKey?: string
  search?: string
  tags?: string[]
  scribe?: string
  year?: number
}

class RailbirdLedgerApi {
  private getAccessTokenSilently: (() => Promise<string>) | null = null;

  setAuth(getAccessTokenSilently: () => Promise<string>) {
    this.getAccessTokenSilently = getAccessTokenSilently;
  }

  private async getHeaders(): Promise<HeadersInit> {
    if (!this.getAccessTokenSilently) {
      throw new Error('Auth not initialized. Call setAuth() first.');
    }
    try {
      const token = await this.getAccessTokenSilently();
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      };
    } catch (error) {
      throw new Error('Authentication failed');
    }
  }

  // Get all entries with pagination and search
  async getEntries(options?: LedgerSearchParams): Promise<LedgerEntriesResponse> {
    const headers = await this.getHeaders();
    
    const params = new URLSearchParams()
    if (options?.limit) params.append('limit', options.limit.toString())
    if (options?.lastKey) params.append('lastKey', options.lastKey)
    if (options?.search) params.append('search', options.search)
    if (options?.scribe) params.append('scribe', options.scribe)
    if (options?.year) params.append('year', options.year.toString())
    if (options?.tags && options.tags.length > 0) {
      params.append('tags', options.tags.join(','))
    }
    
    const url = `${API_BASE_URL}/v1/railbird-ledger${params.toString() ? `?${params}` : ''}`
    
    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error(`Failed to fetch ledger entries: ${response.statusText}`);
    return response.json();
  }

  // Get a single entry
  async getEntry(id: string): Promise<LedgerEntry> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_BASE_URL}/v1/railbird-ledger/${id}`, { headers });
    if (!response.ok) throw new Error(`Failed to fetch ledger entry: ${response.statusText}`);
    return response.json();
  }

  // Create a new entry
  async createEntry(data: CreateLedgerEntryData): Promise<{ id: string, message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_BASE_URL}/v1/railbird-ledger`, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to create ledger entry: ${response.statusText}`);
    }
    return response.json();
  }

  // Update an entry
  async updateEntry(id: string, data: UpdateLedgerEntryData): Promise<{ message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_BASE_URL}/v1/railbird-ledger/${id}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(data)
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to update ledger entry: ${response.statusText}`);
    }
    return response.json();
  }

  // Delete an entry
  async deleteEntry(id: string): Promise<{ message: string }> {
    const headers = await this.getHeaders();
    const response = await fetch(`${API_BASE_URL}/v1/railbird-ledger/${id}`, {
      method: 'DELETE',
      headers
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || `Failed to delete ledger entry: ${response.statusText}`);
    }
    return response.json();
  }

}

export const railbirdLedgerApi = new RailbirdLedgerApi();

export const useRailbirdLedgerApi = () => {
  const { getAccessTokenSilently, isLoading } = useAuth0();
  useEffect(() => {
    if (!isLoading) {
      railbirdLedgerApi.setAuth(getAccessTokenSilently);
    }
  }, [isLoading, getAccessTokenSilently]);
  return railbirdLedgerApi;
};
